import React, { useEffect, useState } from "react";
import { Download, CreditCard } from "lucide-react";
import { Card, PageHeader, StatusPill, Empty, Btn } from "../../components/dash/Kit";
import { api } from "../../lib/api";

export default function Billing() {
  const [invoices, setInvoices] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/invoices").then((r) => setInvoices(r.data)).finally(() => setLoading(false));
    api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="billing-page">
      <PageHeader title="Billing" body="Your invoices, payment method and billing details." />

      <Card testId="payment-method-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-display text-lg font-semibold text-slate-900">Payment Method</p>
            <p className="mt-2 text-sm text-slate-600">
              Payments run through Stripe, supporting <strong>iDEAL</strong>, credit card and debit card for monthly
              recurring subscriptions. You enter your payment details on Stripe's secure checkout page — we never see or
              store your card or bank details.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              You will never be charged extra without clear authorisation.
            </p>
          </div>
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFF3EF] text-[#F95738]">
            <CreditCard className="h-4 w-4" aria-hidden="true" />
          </span>
        </div>
        <Btn variant="outline" testId="update-payment-method-button" className="mt-4" onClick={() => (window.location.href = "/dashboard/subscription")}>
          Manage payment &amp; plan
        </Btn>
      </Card>

      <Card testId="billing-details-card">
        <p className="font-display text-lg font-semibold text-slate-900">Billing Details</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Plan</p>
            <p className="mt-1 text-sm text-slate-800">{sub?.package?.name || "—"}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Amount</p>
            <p className="mt-1 text-sm text-slate-800">€{sub?.subscription?.price ?? "—"} / month</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Next billing date</p>
            <p className="mt-1 text-sm text-slate-800">{sub?.subscription?.next_billing_date || "—"}</p>
          </div>
        </div>
        <p className="mt-4 text-xs text-slate-500">
          Prices are shown excluding VAT where VAT legally applies. Your invoice shows the exact VAT treatment.
        </p>
      </Card>

      <div>
        <p className="mb-3 font-display text-lg font-semibold text-slate-900">Invoices</p>
        {loading ? (
          <Card testId="invoices-loading">Loading invoices…</Card>
        ) : invoices.length === 0 ? (
          <Empty
            testId="invoices-empty"
            title="No invoices yet"
            body="Invoices appear here once billing has been activated for your account."
          />
        ) : (
          <Card testId="invoices-table" className="overflow-x-auto p-0">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
                <tr>
                  {["Invoice", "Date", "Amount", "VAT", "Status", ""].map((h) => (
                    <th key={h} className="px-5 py-3 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                    <td className="px-5 py-3 font-semibold text-slate-900">{inv.invoice_number}</td>
                    <td className="px-5 py-3 text-slate-600">{inv.date}</td>
                    <td className="px-5 py-3 text-slate-600">€{inv.amount}</td>
                    <td className="px-5 py-3 text-slate-600">€{inv.vat ?? 0}</td>
                    <td className="px-5 py-3"><StatusPill status={inv.status} /></td>
                    <td className="px-5 py-3">
                      <Btn variant="ghost" testId={`invoice-download-${inv.id}`}>
                        <Download className="h-4 w-4" /> Download
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}
